import { t } from 'i18next';
import * as yup from 'yup';

import {
  convertDateStringToDate,
  convertToNullWhenNaN,
  parseDateString
} from 'utils/validation';
import { Dayjs } from 'dayjs';

export const leaveOrArrangementReportSchema = yup.object().shape({
  valid_from: yup
    .date()
    .transform(parseDateString)
    .nullable()
    .default(new Date())
    .required(
      t('validationFieldRequired', {
        name: t('validFrom')
      })
    ),
  valid_to: yup
    .date()
    .transform(parseDateString)
    .nullable()
    .default(new Date())
    .min(
      yup.ref('valid_from'),
      t('validationNotBefore', {
        first: t('validFrom'),
        last: t('validTo')
      })
    )
    .required(
      t('validationFieldRequired', {
        name: t('validTo')
      })
    )
});

export const baseReportSchema = yup.object({
  valid_from: leaveOrArrangementReportSchema.fields.valid_from,
  valid_to: leaveOrArrangementReportSchema.fields.valid_to,
  br_id_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN)),
  emp_id_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN)),
  cluster_list: yup.number().nullable().transform(convertToNullWhenNaN),
  pool_list: yup.number().nullable().transform(convertToNullWhenNaN),
  contract_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN))
});

export const projectPlanningSchema = yup.object({
  valid_from: leaveOrArrangementReportSchema.fields.valid_from,
  valid_to: leaveOrArrangementReportSchema.fields.valid_to,
  br_id_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN))
    .when('project_entity_id_list', {
      is: (project_entity_id_list) => project_entity_id_list.length == 0,
      then: yup
        .array()
        .of(yup.number().nullable().transform(convertToNullWhenNaN))
        .min(1, t('planningReport.formErrorMessages.chooseAtLeastOne'))
    }),
  project_entity_id_list: yup
    .array()
    .of(yup.string().nullable().transform(convertToNullWhenNaN)),
  status: yup
    .array()
    .of(yup.string().nullable().transform(convertToNullWhenNaN))
    .default([])
});

export const compactDutyReportSchema = yup.object().shape({
  ...baseReportSchema.fields,
  not_ready_for_realisation_flag: yup.boolean().nullable(),
  dub_trv_flag: yup.boolean().nullable()
});

export const dayRosterSchema = yup.object().shape({
  valid_from: baseReportSchema.fields.valid_from,
  valid_to: baseReportSchema.fields.valid_to,
  br_id_list: baseReportSchema.fields.br_id_list,
  pool_id_list: baseReportSchema.fields.pool_list,
  cluster_id_list: baseReportSchema.fields.cluster_list,
  inc_notes: yup.boolean().nullable()
});

export const requestReportSchema = yup.object().shape({
  ...dayRosterSchema.fields,
  sent_agn_flag: yup.boolean().nullable()
});

export const availabilitySchema = yup.object().shape({
  valid_from: baseReportSchema.fields.valid_from,
  valid_to: baseReportSchema.fields.valid_to,
  flex_only_flag: yup.boolean().nullable().required().default(false),
  no_duty_flag: yup.boolean().nullable(),
  pool_id_list: baseReportSchema.fields.pool_list,
  cluster_id_list: baseReportSchema.fields.cluster_list,
  availability_type_list: yup.string().nullable()
});

export const preferenceSchema = yup.object({
  pref_type_list: yup.string().nullable()
});

export const referenceDateSchema = yup.object({
  reference_date: yup
    .mixed<Dayjs | string>()
    .transform(convertDateStringToDate)
    .required(t('validationFieldRequired', { name: t('referenceDate') }))
    .typeError(t('invalidDate', { field: t('referenceDate') }))
});

export const authSchema = yup.object({
  reference_date: referenceDateSchema.fields.reference_date,
  account_id_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN)),
  group_id_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN))
});

export const basePlanSchema = yup.object({
  br_id_list: baseReportSchema.fields.br_id_list,
  cluster_list: baseReportSchema.fields.cluster_list,
  pool_id_list: baseReportSchema.fields.pool_list
});

export const benefitsSchema = yup.object({
  valid_from: leaveOrArrangementReportSchema.fields.valid_from,
  emp_id_list: baseReportSchema.fields.emp_id_list,
  cluster_list: baseReportSchema.fields.cluster_list,
  pool_id_list: baseReportSchema.fields.pool_list,
  contract_list: baseReportSchema.fields.contract_list
});

export const capplanSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  month_or_week: yup.string().required().default('month'),
  br_id_list: baseReportSchema.fields.br_id_list
});

export const travelExpenseClaimSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields
});

export const travelReportSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  ben_flag: yup.boolean()
});

export const endingBenefitsSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  emp_id_list: baseReportSchema.fields.emp_id_list,
  contract_list: baseReportSchema.fields.contract_list
});

export const hourPerPlanUnitSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  br_id_list: baseReportSchema.fields.br_id_list
});

export const noTravelArrangementSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  contract_list: baseReportSchema.fields.contract_list,
  cc_category_list: yup.string(),
  cc_type_list: yup.string()
});

export const dutyLineExtSchema = yup.object({
  ...leaveOrArrangementReportSchema.fields,
  br_id_list: baseReportSchema.fields.br_id_list,
  emp_br_id_list: yup.number().nullable().transform(convertToNullWhenNaN),
  emp_id_list: baseReportSchema.fields.emp_id_list,
  cluster_list: baseReportSchema.fields.cluster_list,
  pool_list: baseReportSchema.fields.pool_list,
  contract_list: baseReportSchema.fields.contract_list,
  not_ready_for_realisation_flag:
    compactDutyReportSchema.fields.not_ready_for_realisation_flag,
  dub_trv_flag: compactDutyReportSchema.fields.dub_trv_flag
});

export const startAndEndDateSchema = yup.object({
  start: leaveOrArrangementReportSchema.fields.valid_from,
  end: leaveOrArrangementReportSchema.fields.valid_to
});

export const startDateSchema = yup.object({
  valid_from: leaveOrArrangementReportSchema.fields.valid_from
});

export const costOrgSchema = yup.object({
  reference_date: referenceDateSchema.fields.reference_date,
  org_level: yup
    .number()
    .min(1)
    .max(10)
    .nullable()
    .transform(convertToNullWhenNaN)
});

export const auxGroupSchema = yup.object({
  auxiliary_group_list: yup
    .array()
    .of(yup.number().nullable().transform(convertToNullWhenNaN))
});

export const absenceCheckSchema = yup.object({
  valid_from: leaveOrArrangementReportSchema.fields.valid_from,
  valid_to: leaveOrArrangementReportSchema.fields.valid_to,
  dht_id_list: yup
    .number()
    .required(t('validation.valueIsRequired'))
    .nullable()
    .transform(convertToNullWhenNaN)
});

export const detailOverviewSchema = yup.object({
  batch_id: yup
    .number()
    .nullable()
    .transform(convertToNullWhenNaN)
    .required(
      t('validationFieldRequired', {
        name: t('batchId')
      })
    )
});

export const sdEmployeeSchema = yup.object({
  incl_benefit_flag: yup.boolean()
});
